import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { FORM_CONDITION_OPS } from "@domain/form";
import { Alert, Button, Form, Input, InputNumber, Select, Space } from "antd";
import { useTranslation } from "react-i18next";
import type { FormCondition, FormField } from "../types";

interface Props {
	conditions: FormCondition[];
	/**
	 * ⚠️ Faqat **oldingi** va **javob beruvchi** maydonlar. Ro'yxatni
	 * chaqiruvchi tayyorlaydi (`FieldSettingsPanel`) — bu komponent uni
	 * kesmaydi, aks holda invariant ikki joyda yashardi.
	 */
	precedingFields: FormField[];
	onChange: (next: FormCondition[]) => void;
}

type ConditionOp = FormCondition["op"];

/** `answered` da qiymat ma'nosiz — sxemaga tushmasligi kerak. */
function needsValue(op: ConditionOp): boolean {
	return op !== "answered";
}

function ValueInput({
	condition,
	source,
	index,
	onChange,
}: {
	condition: FormCondition;
	source: FormField | undefined;
	index: number;
	onChange: (value: unknown) => void;
}) {
	const { t } = useTranslation("leadForms");

	if (!needsValue(condition.op)) {
		return null;
	}

	const wrap = (node: React.ReactNode) => <span data-testid={`cond-value-${index}`}>{node}</span>;

	if (source && (source.type === "select" || source.type === "multiselect")) {
		return wrap(
			<Select
				className="w-40"
				mode={condition.op === "in" ? "multiple" : undefined}
				value={condition.value as string | string[] | undefined}
				options={(source.options ?? []).map((option) => ({
					value: option.value,
					label: option.label,
				}))}
				onChange={onChange}
			/>
		);
	}

	if (source && (source.type === "number" || source.type === "rating")) {
		return wrap(<InputNumber value={condition.value as number | undefined} onChange={onChange} />);
	}

	if (source && source.type === "boolean") {
		return wrap(
			<Select
				className="w-28"
				value={condition.value as boolean | undefined}
				options={[
					{ value: true, label: t("conditionEditor.boolValue.true") },
					{ value: false, label: t("conditionEditor.boolValue.false") },
				]}
				onChange={onChange}
			/>
		);
	}

	return wrap(
		<Input
			className="w-40"
			value={(condition.value as string | undefined) ?? ""}
			onChange={(event) => onChange(event.target.value)}
		/>
	);
}

/**
 * ⚠️ Shartlar **AND** bilan bog'lanadi (spec §4.3). OR yo'q — kerak
 * bo'lsa ikki alohida maydon yaratiladi.
 */
export function ConditionEditor({ conditions, precedingFields, onChange }: Props) {
	const { t } = useTranslation("leadForms");

	const opOptions = FORM_CONDITION_OPS.map((op) => ({
		value: op,
		label: t(`conditionEditor.op.${op}`),
	}));

	if (precedingFields.length === 0) {
		return (
			<Alert
				type="info"
				showIcon
				message={t("conditionEditor.firstFieldTitle")}
				description={t("conditionEditor.firstFieldDescription")}
			/>
		);
	}

	const update = (index: number, changes: Partial<FormCondition>) =>
		onChange(
			conditions.map((item, position) => (position === index ? { ...item, ...changes } : item))
		);

	return (
		<Form.Item label={t("conditionEditor.label")} help={t("conditionEditor.help")}>
			<Space direction="vertical" className="w-full">
				{conditions.map((condition, index) => {
					const source = precedingFields.find((field) => field.key === condition.field);

					return (
						<Space key={`${condition.field}-${index}`} wrap>
							<Select
								className="w-44"
								value={condition.field}
								onChange={(field) => update(index, { field, value: undefined })}
								options={precedingFields.map((field) => ({
									value: field.key,
									label: <span data-testid={`cond-field-${field.key}`}>{field.label}</span>,
								}))}
							/>
							<Select<ConditionOp>
								className="w-36"
								value={condition.op}
								options={opOptions}
								onChange={(op) =>
									update(index, { op, value: needsValue(op) ? condition.value : undefined })
								}
							/>
							<ValueInput
								condition={condition}
								source={source}
								index={index}
								onChange={(value) => update(index, { value })}
							/>
							<Button
								type="text"
								danger
								data-testid={`cond-remove-${index}`}
								icon={<DeleteOutlined />}
								title={t("conditionEditor.removeCondition")}
								onClick={() => onChange(conditions.filter((_, position) => position !== index))}
							/>
						</Space>
					);
				})}
				<Button
					data-testid="cond-add"
					icon={<PlusOutlined />}
					onClick={() =>
						onChange([
							...conditions,
							// Standart — birinchi mavjud oldingi maydon; bo'sh
							// `field` bilan shart yaratilsa u darhol `unknown_field`
							// xatosiga tushardi.
							{ field: precedingFields[0]?.key ?? "", op: "eq", value: undefined },
						])
					}
				>
					{t("conditionEditor.addCondition")}
				</Button>
			</Space>
		</Form.Item>
	);
}
